import { Power1 } from 'gsap';
import Rectangle from '../../graphics/Rectangle';
import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import { NONE } from '../../constants/DropShadow';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getAlignX,
  getCenterX,
  getCenterY,
  getFixAboveY,
  getFixBelowY,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 2;

export default class OverUnder extends DraggablePreset {
  constructor(app) {
    super(app);
    this.topTextMask = new GraphicMask();
    this.topText = new Text(app);
    this.topText.mask = this.topTextMask.mask;

    this.bottomTextMask = new GraphicMask();
    this.bottomText = new Text(app);
    this.bottomText.mask = this.bottomTextMask.mask;

    this.centerLine = new Rectangle();

    this.container.addChild(this.topTextMask.mask, this.bottomTextMask.mask);

    // We need to add text as the first children so that it gets drawn under the center line
    this.container.addChildAt(this.topText, 0);
    this.container.addChildAt(this.bottomText, 0);
  }

  update(model) {
    model = { ...model, dropShadow: NONE }; // disable drop shadow for this preset
    super.update(model);

    const animationDuration = Math.min(this.duration, MAX_DURATION);
    const centerLineDuration = animationDuration / 4;
    const textMoveDuration = animationDuration / 8;

    // Ensure our primary elements are initialized
    this.topText.text = this.getTextLine(0);
    this.topText.updateStyle(model);

    this.bottomText.text = this.getTextLine(1);
    this.bottomText.updateStyle(model, { fill: this.secondaryColor });

    this.centerLine.height = this.topText.fontSize * 0.1;
    this.centerLine.width = 0;

    // Get the height and width of our root
    const maxTextWidth = Math.max(
      this.topText.innerControls.width,
      this.bottomText.innerControls.width
    );
    const maxHeight =
      this.topText.lineControls.height +
      this.bottomText.lineControls.height +
      this.centerLine.height;

    this.setDraggableRoot(maxHeight, maxTextWidth);

    // position all elements off of root
    this.centerLine.x = getCenterX(this.centerLine, this.root);
    this.centerLine.y = getCenterY(this.centerLine, this.root);
    this.topText.innerControls.x = getAlignX(
      this.topText.innerControls,
      this.root
    );
    this.bottomText.innerControls.x = getAlignX(
      this.bottomText.innerControls,
      this.root
    );
    this.topText.innerControls.y =
      getFixAboveY(this.topText.innerControls, this.centerLine) -
      this.centerLine.height;
    this.bottomText.innerControls.y =
      getFixBelowY(this.bottomText.innerControls, this.centerLine) +
      this.centerLine.height;

    // Mask the text in its current spot
    this.setMaskOnText(this.topText, this.topTextMask);
    this.setMaskOnText(this.bottomText, this.bottomTextMask);

    // re-adjust the text to start positions
    this.topText.innerControls.y = getFixBelowY(
      this.topText.innerControls,
      this.centerLine
    );
    this.bottomText.innerControls.y = getFixAboveY(
      this.bottomText.innerControls,
      this.centerLine
    );

    // set default properties
    this.centerLine.color = this.primaryColor;
    this.topText.alpha = 0;
    this.bottomText.alpha = 0;
    this.centerLine.width = 0;

    // Center Line: expand and then shrink it back
    this.animate(this.centerLine)
      .expandTo(this.root, centerLineDuration)
      .reverseOut();

    // Top Text: slide in from bottom, then slide back down
    this.animate(this.topText.innerControls)
      .to(
        {
          alpha: 1,
          y:
            getFixAboveY(this.topText.innerControls, this.centerLine) -
            this.centerLine.height,
          ease: Power1.easeOut,
          duration: textMoveDuration,
        },
        centerLineDuration
      )
      .reverseOut();

    // Bottom Text: slide in from top, then slide back up
    this.animate(this.bottomText.innerControls)
      .to(
        {
          alpha: 1,
          y:
            getFixBelowY(this.bottomText.innerControls, this.centerLine) +
            this.centerLine.height,
          ease: Power1.easeOut,
          duration: textMoveDuration,
        },
        centerLineDuration + textMoveDuration
      )
      .reverseOut(this.duration - centerLineDuration - textMoveDuration);
  }

  render() {
    super.render();
    this.centerLine.draw(this.graphics);
    this.topTextMask.render();
    this.bottomTextMask.render();
  }
}
